import {
  faAngleDown,
  faAngleUp,
  faGripLines,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTimeFormatter, ZonedDateTime } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  Collapse,
  IconButton,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Box } from '@mui/system';
import { useAuth } from '@teamexos/fit-shared';
import noop from 'lodash/noop';
import { Resizable } from 're-resizable';
import React, {
  createContext,
  PropsWithChildren,
  use,
  useMemo,
  useState,
} from 'react';
import { Divider } from '../components/reusable/Divider';
import Text, { TextKind } from '../components/reusable/Text';
import { useSelfQuery } from '../graphql/types';
import useLocalStorage from '../hooks/useLocalStorage';
import { BLACK2, BORDER_GRAY, SECONDARY_HOVER_GRAY } from '../theme';

const hourMinuteSecondFormat = DateTimeFormatter.ofPattern(
  'hh:mm:ss',
).withLocale(Locale.US);

interface ITrackingEventListContext {
  pushEvent: (id: string, eventName: string, eventData: any) => void;
}

const Context = createContext<ITrackingEventListContext>({
  pushEvent: noop,
});

const TrackingEventListProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { isLoggedIn, isLoading: isAuthLoading } = useAuth();
  const { data } = useSelfQuery({
    skip: !isLoggedIn || isAuthLoading,
  });
  const selfId = data?.self?.id;
  const [enabled] = useLocalStorage<boolean>(
    `${selfId}-toggle-trackingEventLog`,
    false,
  );

  const [events, setEvents] = useState<
    { id: string; eventName: string; eventData: any; time: ZonedDateTime }[]
  >([]);
  const [open, setOpen] = useState(false);

  const context = useMemo(
    () => ({
      pushEvent: (id: string, eventName: string, eventData: any) => {
        if (!enabled) {
          return;
        }
        setEvents((prev) => [
          { id, eventName, eventData, time: ZonedDateTime.now() },
          ...prev,
        ]);
      },
    }),
    [enabled],
  );

  if (!enabled) {
    return <Context.Provider value={context}>{children}</Context.Provider>;
  }

  return (
    <Context.Provider value={context}>
      {children}
      {enabled && (
        <Card
          sx={{
            position: 'fixed',
            bottom: '0',
            left: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            zIndex: 10,
            minWidth: '300px',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <ListItemButton
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 2,
            }}
            onClick={() => setOpen((prev) => !prev)}
          >
            <ListItemText primary="Tracking event List" />

            {open ? (
              <FontAwesomeIcon icon={faAngleDown} />
            ) : (
              <FontAwesomeIcon icon={faAngleUp} />
            )}
          </ListItemButton>

          <Collapse
            in={open}
            sx={{
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                overflowY: 'auto',
                paddingTop: 1,
              }}
            >
              <Resizable
                maxHeight="75vh"
                enable={{
                  top: true,
                }}
                handleComponent={{
                  top: (
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faGripLines}
                        style={{ position: 'absolute' }}
                      />
                      <Divider $marginBottom={0} $marginTop={0} />
                    </Box>
                  ),
                }}
              >
                <Box>
                  {events.map((event) => (
                    <React.Fragment key={event.id}>
                      <Accordion
                        sx={{
                          margin: 0,
                          width: '100%',
                          minHeight: 'unset',
                          boxShadow: 'none',
                          '&.MuiAccordionSummary-content, &.Mui, &.Mui-expanded':
                            {
                              margin: 0,
                            },
                        }}
                      >
                        <AccordionSummary
                          sx={{
                            minHeight: 'unset',
                            '&.Mui-expanded': {
                              minHeight: 'unset',
                            },
                            '& .MuiAccordionSummary-content': {
                              margin: '6px 0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            },
                            '& .MuiAccordionSummary-content.Mui-expanded': {
                              margin: '6px 0',
                            },
                          }}
                        >
                          <Text kind={TextKind.LabelL}>
                            {event.time.format(hourMinuteSecondFormat)}{' '}
                            {event.eventName}
                          </Text>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setEvents((prev) =>
                                prev.filter(
                                  (prevEvent) => prevEvent.id !== event.id,
                                ),
                              );
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faTimes}
                              fontSize={16}
                              color={BLACK2}
                            />
                          </IconButton>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            padding: '8px',
                            background: SECONDARY_HOVER_GRAY,
                          }}
                        >
                          <details>
                            <summary style={{ cursor: 'pointer' }}>
                              <Text kind={TextKind.LabelM}>Generic</Text>
                            </summary>
                            <pre style={{ margin: 0 }}>
                              {JSON.stringify(
                                event.eventData.genericData,
                                null,
                                2,
                              )}
                            </pre>
                          </details>
                          <details open>
                            <summary style={{ cursor: 'pointer' }}>
                              <Text kind={TextKind.LabelM}>Specific</Text>
                            </summary>
                            <pre style={{ margin: 0 }}>
                              {JSON.stringify(
                                event.eventData.specificData,
                                null,
                                2,
                              )}
                            </pre>
                          </details>
                        </AccordionDetails>
                      </Accordion>
                      <Divider $marginBottom={0} $marginTop={0} />
                    </React.Fragment>
                  ))}
                </Box>
              </Resizable>
            </Box>
            <Box sx={{ borderTop: `1px solid ${BORDER_GRAY}` }}>
              <Button
                fullWidth
                variant="text"
                color="secondary"
                size="small"
                onClick={() => setEvents([])}
              >
                Clear
              </Button>
            </Box>
          </Collapse>
        </Card>
      )}
    </Context.Provider>
  );
};

export const useTrackingEventListContext = () => use(Context);

export default TrackingEventListProvider;
